package one.devsbox.www.ui.users.activity

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.Crossfade
import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import compose.assets.Svg
import compose.assets.svg.*
import one.devsbox.shared.ads.SharedIO
import one.devsbox.shared.ads.TargetType
import one.devsbox.shared.ads.UserAction
import one.devsbox.www.tools.ArgsId
import one.devsbox.www.ui.users.*
import one.devsbox.www.ui.widgets.FullscreenErrorUI
import one.devsbox.www.ui.widgets.FullscreenLoadingUI
import one.devsbox.www.ui.widgets.LabelField
import one.devsbox.www.ui.widgets.LabelSection
import one.devsbox.www.ui.widgets.SearchBox
import one.devsbox.www.www_portal.generated.resources.*
import org.jetbrains.compose.resources.stringResource
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.utils.DataState
import tk.mallumo.utils.noDiacriticsLower

@Composable
@ComposableNavNode(ArgsId::class)
fun UsersActivityUI() {
    with(UsersActivityCtx.get()) {
        ContentUsersActivityUI()
    }
}

@Composable
context(ctx: UsersActivityCtx)
private fun ContentUsersActivityUI() {
    Box(modifier = Modifier.fillMaxSize().background(MaterialTheme.colorScheme.background)) {
        FullScreens()

        ContentComposition()
    }
}

@Composable
context(ctx: UsersActivityCtx)
private fun FullScreens() {
    val state = ctx.vm.state
    if (state.entry == null) {
        Crossfade(targetState = state) { currentState ->
            when (currentState) {
                is DataState.Idle,
                is DataState.Loading -> FullscreenLoadingUI()

                is DataState.Error -> {
                    FullscreenErrorUI(
                        message = currentState.message,
                        onBack = { ctx.action.back() },
                        onReload = { ctx.action.reload() }
                    )
                }

                is DataState.Result -> Unit
            }
        }
    }
}

@Composable
context(ctx: UsersActivityCtx)
private fun BoxScope.ContentComposition() {
    BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
        val isWide = maxWidth >= 1000.dp
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(16.dp)
        ) {
            // Header Row
            Text(
                text = stringResource(Res.string.ui_users_activity_title),
                style = MaterialTheme.typography.headlineLarge,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onSurface,
                modifier = Modifier.fillMaxWidth().padding(bottom = 16.dp),
            )

            // Search and Filters Toolbar
            SearchBox(
                state = ctx.vm.search,
                hint = stringResource(Res.string.ui_users_listing_search_placeholder),
                modifier = Modifier.fillMaxWidth().padding(bottom = 16.dp),
                actions = listOf(
                    {
                        ActionFilterDropdown(
                            modifier = Modifier.align(Alignment.CenterVertically).width(240.dp),
                            selectedActions = ctx.vm.selectedActions,
                            onToggle = { ctx.action.toggleAction(it) }
                        )
                    },
                    {
                        TargetFilterDropdown(
                            modifier = Modifier.align(Alignment.CenterVertically).width(240.dp),
                            selectedTarget = ctx.vm.selectedTarget,
                            onSelected = { ctx.action.setTarget(it) }
                        )
                    }
                )
            )

            Crossfade(
                targetState = ctx.vm.state,
                modifier = Modifier.fillMaxWidth().weight(1f)
            ) { state ->
                state.entry?.also {
                    SuccessUI(it, isWide)
                }
            }
        }
    }
}

@Composable
context(ctx: UsersActivityCtx)
private fun SuccessUI(data: SharedIO.UserActivities, isWide: Boolean) {
    // Audit Logs Table Card
    Card(
        modifier = Modifier.fillMaxWidth().fillMaxHeight(),
        colors = CardDefaults.cardColors(containerColor = Color.Transparent),
    ) {
        Column(modifier = Modifier.padding(vertical = 16.dp).fillMaxSize()) {
            LabelSection(text = stringResource(Res.string.ui_users_activity_title))
            Spacer(modifier = Modifier.height(16.dp))
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)

            // Table Headers (Visible only in wide viewports for proper tabular presentation)
            if (isWide) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(vertical = 12.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Box(modifier = Modifier.weight(2f)) {
                        LabelField(text = stringResource(Res.string.ui_users_activity_header_timestamp))
                    }
                    Box(modifier = Modifier.weight(1.5f)) {
                        LabelField(text = stringResource(Res.string.ui_users_activity_header_action))
                    }
                    Box(modifier = Modifier.weight(3.5f)) {
                        LabelField(text = stringResource(Res.string.ui_users_activity_header_details))
                    }
                }
                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
            }

            val filteredItems = data.items.filter { log ->
                val matchesSearch = ctx.vm.search.text.trim().let {
                    it.isEmpty() || log.name.noDiacriticsLower.contains(ctx.vm.search.text.toString().noDiacriticsLower)
                }
                val matchesTarget =
                    ctx.vm.selectedTarget == null || TargetType[log.target] == ctx.vm.selectedTarget
                matchesSearch && matchesTarget
            }.sortedByDescending { it.dt }

            if (filteredItems.isEmpty()) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .weight(1f),
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = stringResource(Res.string.ui_users_activity_empty),
                        style = MaterialTheme.typography.bodyLarge,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            } else {
                AnimatedContent(
                    targetState = filteredItems,
                    modifier = Modifier.weight(1f)
                ) { currentList ->
                    LazyColumn(
                        modifier = Modifier.fillMaxSize(),
                        contentPadding = PaddingValues(bottom = 16.dp)
                    ) {
                        itemsIndexed(currentList) { index, log ->
                            if (isWide) {
                                WideLogItemRow(log)
                            } else {
                                CompactLogItemRow(log)
                            }
                            if (index < currentList.lastIndex) {
                                HorizontalDivider(
                                    color = MaterialTheme.colorScheme.outlineVariant.copy(
                                        alpha = 0.5f
                                    )
                                )
                            }
                        }

                        if (data.items.size < data.count) {
                            item {
                                LoadMoreButton(
                                    isLoading = ctx.vm.loadingMore,
                                    onClick = { ctx.action.loadMore() }
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ActionFilterDropdown(
    modifier: Modifier = Modifier,
    selectedActions: List<UserAction>,
    onToggle: (UserAction) -> Unit
) {
    var expanded by remember { mutableStateOf(false) }
    val rotation by animateFloatAsState(if (expanded) -90f else 90f)

    val selectedActionsText = if (selectedActions.isEmpty()) {
        stringResource(Res.string.ui_users_activity_filter_all)
    } else {
        selectedActions.map { stringResource(it.text()) }.joinToString(", ")
    }

    Box(modifier) {
        OutlinedButton(
            onClick = { expanded = true },
            shape = RoundedCornerShape(4.dp),
            contentPadding = PaddingValues(horizontal = 12.dp, vertical = 8.dp),
            modifier = Modifier.height(48.dp),
            border = BorderStroke(0.5.dp, Color.Transparent)
        ) {
            Text(
                text = stringResource(Res.string.ui_users_activity_filter_action),
                fontSize = 13.sp,
                color = MaterialTheme.colorScheme.onSurface,
                fontWeight = FontWeight.Bold
            )
            Spacer(Modifier.width(8.dp))
            if (selectedActions.isEmpty()) {
                Text(
                    text = stringResource(Res.string.ui_users_activity_filter_all),
                    fontSize = 13.sp,
                    color = MaterialTheme.colorScheme.primary,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.weight(1f)
                )
            } else {
                Row(
                    modifier = Modifier.weight(1f),
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    selectedActions.forEach { action ->
                        Icon(
                            imageVector = action.icon(),
                            contentDescription = null,
                            modifier = Modifier.size(16.dp),
                            tint = action.color()
                        )
                    }
                }
            }
            Icon(
                imageVector = Svg.ChevronRight,
                contentDescription = null,
                modifier = Modifier.size(12.dp).rotate(rotation),
                tint = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }

        DropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false },
            modifier = Modifier.background(MaterialTheme.colorScheme.surface)
        ) {
            UserAction.entries.forEach { action ->
                DropdownMenuItem(
                    text = {
                        val isSelected = selectedActions.contains(action)
                        val color =
                            if (isSelected) action.color() else MaterialTheme.colorScheme.onBackground
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Checkbox(
                                checked = isSelected,
                                onCheckedChange = null,
                                colors = CheckboxDefaults.colors(checkedColor = action.color())
                            )
                            Spacer(Modifier.width(8.dp))
                            Icon(
                                imageVector = action.icon(),
                                contentDescription = null,
                                modifier = Modifier.size(18.dp),
                                tint = color
                            )
                            Spacer(Modifier.width(8.dp))
                            Text(
                                text = stringResource(action.text()),
                                color = color,
                                fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Normal
                            )
                        }
                    },
                    onClick = { onToggle(action) }
                )
            }
        }
    }
}

@Composable
private fun TargetFilterDropdown(
    modifier: Modifier = Modifier,
    selectedTarget: TargetType?,
    onSelected: (TargetType?) -> Unit
) {
    var expanded by remember { mutableStateOf(false) }
    val rotation by animateFloatAsState(if (expanded) -90f else 90f)

    Box(modifier) {
        OutlinedButton(
            onClick = { expanded = true },
            shape = RoundedCornerShape(4.dp),
            contentPadding = PaddingValues(horizontal = 12.dp, vertical = 8.dp),
            modifier = Modifier.height(48.dp),
            border = BorderStroke(0.5.dp, Color.Transparent)
        ) {
            Text(
                text = stringResource(Res.string.ui_users_activity_filter_target),
                fontSize = 13.sp,
                color = MaterialTheme.colorScheme.onSurface,
                fontWeight = FontWeight.Bold
            )
            Spacer(Modifier.width(8.dp))
            Box(
                modifier = Modifier.weight(1f),
                contentAlignment = Alignment.CenterStart
            ) {
                if (selectedTarget == null) {
                    Text(
                        text = stringResource(Res.string.ui_users_activity_filter_all),
                        fontSize = 13.sp,
                        color = MaterialTheme.colorScheme.primary,
                        fontWeight = FontWeight.Bold
                    )
                } else {
                    Icon(
                        imageVector = selectedTarget.icon(),
                        contentDescription = null,
                        modifier = Modifier.size(16.dp),
                        tint = MaterialTheme.colorScheme.primary
                    )
                }
            }
            Icon(
                imageVector = Svg.ChevronRight,
                contentDescription = null,
                modifier = Modifier.size(12.dp).rotate(rotation),
                tint = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }

        DropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false },
            modifier = Modifier.background(MaterialTheme.colorScheme.surface)
        ) {
            DropdownMenuItem(
                text = {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Icon(
                            imageVector = Svg.GridOutline,
                            contentDescription = null,
                            modifier = Modifier.size(18.dp),
                            tint = if (selectedTarget == null) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onBackground
                        )
                        Spacer(Modifier.width(8.dp))
                        Text(
                            text = stringResource(Res.string.ui_users_activity_filter_all),
                            fontWeight = if (selectedTarget == null) FontWeight.Bold else FontWeight.Normal,
                            color = if (selectedTarget == null) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onBackground
                        )
                    }
                },
                onClick = {
                    onSelected(null)
                    expanded = false
                }
            )
            TargetType.entries.forEach { target ->
                DropdownMenuItem(
                    text = {
                        val isSelected = selectedTarget == target
                        val color =
                            if (isSelected) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onBackground
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Icon(
                                imageVector = target.icon(),
                                contentDescription = null,
                                modifier = Modifier.size(18.dp),
                                tint = color
                            )
                            Spacer(Modifier.width(8.dp))
                            Text(
                                text = stringResource(target.text(0)),
                                color = color,
                                fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Normal
                            )
                        }
                    },
                    onClick = {
                        onSelected(target)
                        expanded = false
                    }
                )
            }
        }
    }
}

@Composable
private fun WideLogItemRow(log: SharedIO.UserActivity) {
    val action = UserAction[log.action]
    val target = TargetType[log.target]
    val actionColor = action?.color() ?: MaterialTheme.colorScheme.onSurfaceVariant

    val actionText = action?.let { stringResource(it.text()) } ?: "ACTION"
    val targetText = target?.let { stringResource(it.text(log.idTarget)) } ?: "TARGET"

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 16.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Timestamp
        Text(
            text = log.dt.formatAsLastActivity(),
            modifier = Modifier.weight(2f),
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )

        // Action Name
        Text(
            text = "$actionText $targetText",
            modifier = Modifier.weight(1.5f),
            style = MaterialTheme.typography.bodyMedium,
            fontWeight = FontWeight.Bold,
            color = actionColor
        )

        // Details
        Text(
            text = log.name,
            modifier = Modifier.weight(3.5f),
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurface
        )
    }
}

@Composable
private fun CompactLogItemRow(log: SharedIO.UserActivity) {
    val action = UserAction[log.action]
    val target = TargetType[log.target]
    val actionColor = action?.color() ?: MaterialTheme.colorScheme.onSurfaceVariant

    val actionText = action?.let { stringResource(it.text()) } ?: "ACTION"
    val targetText = target?.let { stringResource(it.text(log.idTarget)) } ?: "TARGET"

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 12.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = "$actionText $targetText",
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.Bold,
                color = actionColor
            )
            Text(
                text = log.dt.formatAsLastActivity(),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
        Spacer(modifier = Modifier.height(4.dp))
        Text(
            text = log.name,
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurface
        )
    }
}

@Composable
private fun LoadMoreButton(isLoading: Boolean, onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 32.dp),
        contentAlignment = Alignment.Center
    ) {
        if (isLoading) {
            CircularProgressIndicator(modifier = Modifier.size(24.dp))
        } else {
            OutlinedButton(
                onClick = onClick,
                shape = RoundedCornerShape(4.dp),
                colors = ButtonDefaults.outlinedButtonColors(contentColor = MaterialTheme.colorScheme.primary),
                border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
                modifier = Modifier.width(200.dp).height(48.dp)
            ) {
                Text(
                    stringResource(Res.string.ui_users_listing_btn_load_more),
                    fontWeight = FontWeight.Bold
                )
            }
        }
    }
}
